import { Accordion, AccordionStylesNames, Box, Flex, MantineStyleProp, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type SectionCardProps = {
  value: string;
  title: string;
  count: number;
  countNoun?: { one: string; many: string };
  titleMt?: number;
  rightSection: ReactNode;
  rightSectionPlacement?: 'overlay' | 'inline';
  variant?: string;
  bg?: string;
  chevron?: ReactNode;
  styles?: Partial<Record<AccordionStylesNames, React.CSSProperties>>;
  style?: MantineStyleProp;
  openValue?: string | null;
  onOpenChange?: (value: string | null) => void;
  controlTestId?: string;
  countTestId?: string;
  panelTestId?: string;
  children: ReactNode;
};

const DEFAULT_COUNT_NOUN = { one: 'source', many: 'sources' };

export default function SectionCard({
  value,
  title,
  count,
  countNoun = DEFAULT_COUNT_NOUN,
  titleMt,
  rightSection,
  rightSectionPlacement = 'overlay',
  variant,
  bg,
  chevron,
  styles,
  style,
  openValue,
  onOpenChange,
  controlTestId,
  countTestId,
  panelTestId,
  children,
}: SectionCardProps) {
  const heading = (
    <>
      <Text fw={FONT_WEIGHTS.semibold} size="xs" c="gray.4" data-testid={countTestId}>
        {count} {count === 1 ? countNoun.one : countNoun.many}
      </Text>
      <Text fw={FONT_WEIGHTS.bold} size="lg" c="gray.10" mt={titleMt}>
        {title}
      </Text>
    </>
  );

  const controlledProps =
    openValue !== undefined ? { value: openValue, onChange: onOpenChange } : {};

  return (
    <Accordion
      variant={variant}
      chevron={chevron}
      bg={bg}
      styles={styles}
      style={style}
      {...controlledProps}
    >
      <Accordion.Item value={value} role="region">
        {rightSectionPlacement === 'overlay' ? (
          <Box pos="relative">
            <Accordion.Control data-testid={controlTestId}>{heading}</Accordion.Control>
            {rightSection}
          </Box>
        ) : (
          <Accordion.Control data-testid={controlTestId}>
            <Flex justify="space-between" align="center">
              <Box>{heading}</Box>
              {rightSection}
            </Flex>
          </Accordion.Control>
        )}
        <Accordion.Panel data-testid={panelTestId}>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
